/**
 * Admin Settings Component
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useAdminSettings hook.
 */
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Palette, Settings, Zap, FolderTree, Scale, ShieldCheck, ShoppingCart, UserPlus, TrendingUp, Bell, LayoutDashboard, BarChart3, Users, Upload, LifeBuoy, LineChart, PackageCheck, Ban } from 'lucide-react';
import PerformanceSettings from '../settings/PerformanceSettings';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { useIsSupplier } from '../../hooks/usePortalMode';
import { canOpenAdminSetup, isCustomerSideUser, isHelixPlatformUser } from '../../lib/adminSetupAccess';

// Import modular components
import {
  useAdminSettings,
  BrandingTab,
  PlatformTab,
  FeaturesTab,
  DashboardTabsTab,
  PurchasingTabsTab,
  QualityTabsTab,
  SupplierCardTab,
  SupplierProfileLayoutTab,
  HelpLayoutTab,
  DataImportTilesTab,
  HierarchyTab,
  LegalTab,
  QualityTab,
  PurchasingTab,
  ForecastSettingsTab,
  OnboardingTab,
  NotificationSetupTab,
  AnalyticsTab,
  OTIFExclusionsTab,
  CallOffConfigTab,
} from './settings';

/**
 * The legacy /admin page. It has no menu entry, but the URL still resolves.
 *
 * CR-8217: only Helix and customer admins may open it — the same users who
 * may open Admin Setup, minus logistics admins, since every tab here is
 * customer or platform configuration. A supplier admin used to get the full
 * page and "saved successfully" on every tab.
 *
 * @returns The settings page, or a no-access message
 */
export default function AdminSettings() {
  const { user } = useAuth();
  const inSupplierView = useIsSupplier();
  if (!canOpenAdminSetup(user, inSupplierView) || !isCustomerSideUser(user)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You do not have access to Admin Settings.</p>
      </div>
    );
  }
  return <AdminSettingsContent />;
}

function AdminSettingsContent() {
  const { user } = useAuth();
  const isSuperuser = Boolean(user?.is_superuser);
  // CR-8238: dashboard / tab / tile visibility is platform-wide; only Helix saves it.
  const isHelix = isHelixPlatformUser(user);
  // Mirrors backend IsHelixOrCompanyAdmin (same expression as AdminSetup):
  // superuser, Helix platform user, or a company admin. Notification Setup
  // writes tenant-wide delivery config, so non-admins never reach the tab.
  const isAdmin = Boolean(
    user?.is_superuser || user?.company_type === 'helix' || user?.is_company_admin,
  );

  // CR-7259. This legacy /admin page renders the same PerformanceSettings
  // screen as Admin Setup, so it needs the same gate — without it a site
  // admin reaching /admin from a bookmark gets the tab and a wall of 403s.
  // Mirrors backend is_customer_group_admin().
  const isGroupAdmin = Boolean(
    user?.company_type === 'helix' || user?.is_group_admin,
  );
  const { isFeatureEnabled } = useMenuSettings();
  // Call-off config tab is only relevant when this customer has the
  // per-supplier call-off flag enabled on at least one relationship.
  const showCallOffConfig = isFeatureEnabled('switch_call_off_tab');
  const [activeTab, setActiveTab] = useState('platform');
  const unsaved = useUnsavedChangesGuard();
  const {
    // Theme state
    themeSettings,
    setThemeSettings,

    // Platform state
    platformSettings,
    setPlatformSettings,

    // Barratt flags
    barrattFlags,
    setBarrattFlags,

    // Dashboard tab visibility (CR-4441)
    dashboardTabs,
    setDashboardTabs,
    supplierDashboardTabs,
    setSupplierDashboardTabs,
    purchasingTabs,
    setPurchasingTabs,
    savePurchasingTabs,
    qualityTabs,
    setQualityTabs,
    saveQualityTabs,
    supplierCardFields,
    setSupplierCardFields,
    saveSupplierCardFields,
    dataImportTiles,
    setDataImportTiles,
    saveDataImportTiles,

    // File upload
    saving,

    // Actions
    saveThemeSettings,
    savePlatformSettings,
    saveDashboardTabs,
    handleFileUpload,
    resetColorsToDefault,
  } = useAdminSettings();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage platform configuration, branding, and feature toggles
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-medium">Scope:</span>
          <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
            Platform-wide
          </span>
          <span className="text-xs">(Group/Company level overrides available in Hierarchy tab)</span>
        </div>
      </div>

      {/* Settings Tabs — controlled so a tab holding unsaved work (Notification
          Setup stages its grid) can refuse the switch instead of discarding it. */}
      <Tabs
        value={activeTab}
        onValueChange={(next) => { if (unsaved.confirmDiscard()) setActiveTab(next); }}
        className="space-y-4"
      >
        {/* CR-4816: 15-16 tabs crammed into equal grid columns collided on all
            reasonable widths — labels clipped and overlapped icons. Switch to
            an inline-flex bar with horizontal scroll so each tab sizes to its
            own content and the row overflows cleanly. */}
        <TabsList className="inline-flex w-full items-center justify-start gap-1 overflow-x-auto whitespace-nowrap">
          <TabsTrigger value="branding">
            <Palette className="h-4 w-4 mr-2" />
            Branding
          </TabsTrigger>
          {isHelix && (
            <TabsTrigger value="dashboard-tabs">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
          )}
          {isHelix && (
            <TabsTrigger value="supplier-card">
              <Users className="h-4 w-4 mr-2" />
              Supplier Card
            </TabsTrigger>
          )}
          <TabsTrigger value="supplier-profile">
            <Users className="h-4 w-4 mr-2" />
            Supplier Profile
          </TabsTrigger>
          {isHelix && (
            <TabsTrigger value="data-import">
              <Upload className="h-4 w-4 mr-2" />
              Data Import
            </TabsTrigger>
          )}
          <TabsTrigger value="platform">
            <Settings className="h-4 w-4 mr-2" />
            Platform
          </TabsTrigger>
          <TabsTrigger value="features">
            <Zap className="h-4 w-4 mr-2" />
            Features
          </TabsTrigger>
          <TabsTrigger value="quality">
            <ShieldCheck className="h-4 w-4 mr-2" />
            Quality
          </TabsTrigger>
          <TabsTrigger value="purchasing">
            <ShoppingCart className="h-4 w-4 mr-2" />
            Purchasing
          </TabsTrigger>
          {showCallOffConfig && (
            <TabsTrigger value="call-off-config">
              <PackageCheck className="h-4 w-4 mr-2" />
              Call-Off
            </TabsTrigger>
          )}
          <TabsTrigger value="forecast">
            <LineChart className="h-4 w-4 mr-2" />
            Forecast
          </TabsTrigger>
          <TabsTrigger value="onboarding">
            <UserPlus className="h-4 w-4 mr-2" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="hierarchy">
            <FolderTree className="h-4 w-4 mr-2" />
            Hierarchy
          </TabsTrigger>
<TabsTrigger value="legal">
            <Scale className="h-4 w-4 mr-2" />
            Legal
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="notifications">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
          )}
          {isGroupAdmin && (
            <TabsTrigger value="performance">
              <TrendingUp className="h-4 w-4 mr-2" />
              Performance
            </TabsTrigger>
          )}
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="otif-exclusions">
            <Ban className="h-4 w-4 mr-2" />
            OTIF Exclusions
          </TabsTrigger>
          {isSuperuser && (
            <TabsTrigger value="help-layout">
              <LifeBuoy className="h-4 w-4 mr-2" />
              Help
            </TabsTrigger>
          )}
        </TabsList>

        {/* BRANDING TAB */}
        <TabsContent value="branding">
          <BrandingTab
            themeSettings={themeSettings}
            saving={saving}
            onThemeSettingsChange={setThemeSettings}
            onSave={saveThemeSettings}
            onResetColors={resetColorsToDefault}
            onFileUpload={handleFileUpload}
          />
        </TabsContent>

        {/* DASHBOARD TABS TAB (CR-4441) */}
        {isHelix && <TabsContent value="dashboard-tabs">
          <DashboardTabsTab
            dashboardTabs={dashboardTabs}
            supplierDashboardTabs={supplierDashboardTabs}
            saving={saving}
            onDashboardTabsChange={setDashboardTabs}
            onSupplierDashboardTabsChange={setSupplierDashboardTabs}
            onSave={saveDashboardTabs}
          />
        </TabsContent>}

        {/* SUPPLIER CARD TAB */}
        {isHelix && <TabsContent value="supplier-card">
          <SupplierCardTab
            supplierCardFields={supplierCardFields}
            saving={saving}
            onSupplierCardFieldsChange={setSupplierCardFields}
            onSave={saveSupplierCardFields}
          />
        </TabsContent>}

        {/* SUPPLIER PROFILE LAYOUT TAB */}
        <TabsContent value="supplier-profile">
          <SupplierProfileLayoutTab />
        </TabsContent>

        {/* HELP & SUPPORT LAYOUT TAB — superuser-only */}
        {isSuperuser && (
          <TabsContent value="help-layout">
            <HelpLayoutTab />
          </TabsContent>
        )}

        {/* DATA IMPORT TAB */}
        {isHelix && <TabsContent value="data-import">
          <DataImportTilesTab
            dataImportTiles={dataImportTiles}
            saving={saving}
            onDataImportTilesChange={setDataImportTiles}
            onSave={saveDataImportTiles}
          />
        </TabsContent>}

        {/* PLATFORM TAB */}
        <TabsContent value="platform">
          <PlatformTab
            platformSettings={platformSettings}
            saving={saving}
            onPlatformSettingsChange={setPlatformSettings}
            onSave={savePlatformSettings}
            purchasingTabs={purchasingTabs}
            onPurchasingTabsChange={setPurchasingTabs}
            onSavePurchasingTabs={savePurchasingTabs}
            qualityTabs={qualityTabs}
            onQualityTabsChange={setQualityTabs}
            onSaveQualityTabs={saveQualityTabs}
          />
        </TabsContent>

        {/* FEATURES TAB */}
        <TabsContent value="features">
          <FeaturesTab
            platformSettings={platformSettings}
            barrattFlags={barrattFlags}
            saving={saving}
            onPlatformSettingsChange={setPlatformSettings}
            onBarrattFlagsChange={setBarrattFlags}
            onSave={savePlatformSettings}
          />
        </TabsContent>

        {/* QUALITY TAB */}
        <TabsContent value="quality">
          <QualityTab />
          {isHelix && <div className="mt-6">
            <QualityTabsTab
              qualityTabs={qualityTabs}
              saving={saving}
              onQualityTabsChange={setQualityTabs}
              onSave={saveQualityTabs}
            />
          </div>}
        </TabsContent>

        {/* PURCHASING TAB */}
        <TabsContent value="purchasing">
          <PurchasingTab />
          {isHelix && <div className="mt-6">
            <PurchasingTabsTab
              purchasingTabs={purchasingTabs}
              saving={saving}
              onPurchasingTabsChange={setPurchasingTabs}
              onSave={savePurchasingTabs}
            />
          </div>}
        </TabsContent>

        {/* CALL-OFF CONFIG TAB */}
        {showCallOffConfig && (
          <TabsContent value="call-off-config">
            <CallOffConfigTab />
          </TabsContent>
        )}

        {/* FORECAST TAB */}
        <TabsContent value="forecast">
          <ForecastSettingsTab
            barrattFlags={barrattFlags}
            saving={saving}
            onBarrattFlagsChange={setBarrattFlags}
            onSave={savePlatformSettings}
          />
        </TabsContent>

        {/* ONBOARDING TAB */}
        <TabsContent value="onboarding">
          <OnboardingTab
            onboardingEnabled={platformSettings.supplierOnboardingEnabled}
            onToggleOnboarding={(enabled) => {
              // Saved from `next`, not from state — see AdminSetup.tsx.
              const next = { ...platformSettings, supplierOnboardingEnabled: enabled };
              setPlatformSettings(next);
              savePlatformSettings(next);
            }}
          />
        </TabsContent>

        {/* HIERARCHY TAB */}
        <TabsContent value="hierarchy">
          <HierarchyTab />
        </TabsContent>

{/* LEGAL TAB */}
        <TabsContent value="legal">
          <LegalTab />
        </TabsContent>

        {/* NOTIFICATION SETUP TAB — per-event delivery config + master switches */}
        {isAdmin && (
          <TabsContent value="notifications" className="space-y-4">
            <NotificationSetupTab
              platformSettings={platformSettings}
              saving={saving}
              onPlatformSettingsChange={setPlatformSettings}
              onSave={savePlatformSettings}
              onUnsavedChangesChange={unsaved.setUnsavedCount}
            />
          </TabsContent>
        )}

        {/* PERFORMANCE TAB — group-level configuration (CR-7259) */}
        {isGroupAdmin && (
          <TabsContent value="performance" className="space-y-4">
            <PerformanceSettings />
          </TabsContent>
        )}

        {/* ANALYTICS TAB — toggle dataset visibility per customer */}
        <TabsContent value="analytics" className="space-y-4">
          <AnalyticsTab />
        </TabsContent>

        {/* OTIF EXCLUSIONS TAB — schedule POs left out of OTIF calcs */}
        <TabsContent value="otif-exclusions" className="space-y-4">
          <OTIFExclusionsTab />
        </TabsContent>

      </Tabs>
    </div>
  );
}
